import React from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import SaveIcon from '@mui/icons-material/SaveRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import TypeChip, { TypeChipProvider } from '../../components/showClock/TypeChip'
import ClockDurationFields from '../../components/showClock/ClockDurationFields'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { getClockMeta, listClockTemplates, type ClockMetaResponse, type ClockTemplateSummary } from '../../api/showClock'
import {
  attachClockDayItemPrep,
  createClockDay,
  createClockDayItem,
  deleteClockDay,
  deleteClockDayItem,
  detachClockDayItemPrep,
  getClockDay,
  listClockDayItemPreps,
  listClockDayItems,
  listClockDays,
  lookupClockDay,
  reorderClockDayItemPreps,
  reorderClockDayItems,
  updateClockDay,
  updateClockDayItem,
  type ClockDay,
  type ClockDayItem,
  type ClockDayItemPrep,
} from '../../api/showClockDays'
import {
  addPrepItemToMyPrep,
  fetchPrepItemDetail,
  listMyPrepItems,
  removePrepItemFromMyPrep,
  searchPrepItems,
  type PrepDetailResponse,
  type PrepListItem,
} from '../../api/prep'
import PrepDetailDrawer from '../../components/prep/PrepDetailDrawer'
import PrepPicker from '../../components/showClock/PrepPicker'
import { usePageTitle } from '../../components/common/PageTitleContext'
import type { AppOutletContext } from '../../app/AppShell'
import {
  formatClockDuration,
  formatClockTimeLabel,
  formatClockTimeLabel24s,
  resolveClockItemTypes,
  type ClockItemTypeMeta,
} from '../../utils/showClock'
import { useDisplayTitles } from '../../utils/clockDisplayTitle'
import { stationDayDiff, stationLocalDateTimeToEpochMs } from '../../utils/time'

const SORTABLE_DRAG_DISTANCE = 6

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? fallback)
  }
  return fallback
}

const formatDayDate = (value: string): string => {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(date)
}

const extractTime = (value: string | null): string => {
  if (!value) {
    return ''
  }
  const cleaned = value.replace('T', ' ')
  const parts = cleaned.split(' ')
  const time = parts[1] ?? parts[0]
  return time.slice(0, 5)
}

const compareDays = (a: ClockDay, b: ClockDay): number => {
  if (a.date === b.date) {
    return b.id - a.id
  }
  return a.date > b.date ? -1 : 1
}

const formatDayWindowLabel = (day: ClockDay): string | null => {
  const windowStart = extractTime(day.window_start)
  const windowEnd = extractTime(day.window_end)
  if (windowStart && windowEnd) {
    return `${windowStart}–${windowEnd}`
  }
  if (windowStart || windowEnd) {
    return windowStart ?? windowEnd ?? null
  }
  return null
}

const formatDayOptionLabel = (day: ClockDay): string => {
  const baseLabel = day.title?.trim() || formatDayDate(day.date)
  const windowLabel = formatDayWindowLabel(day)
  if (windowLabel) {
    return `${baseLabel} — ${windowLabel}`
  }
  return baseLabel
}

const isUpcomingDay = (day: ClockDay, stationTz?: string | null, referenceEpoch: number = Date.now()): boolean => {
  const dayEpoch = stationLocalDateTimeToEpochMs(`${day.date}T00:00`, stationTz)
  if (!Number.isFinite(dayEpoch ?? Number.NaN)) {
    return true
  }
  return stationDayDiff(dayEpoch!, referenceEpoch, stationTz) >= 0
}

type DayMetaDraft = {
  title: string
  windowStart: string
  windowEnd: string
}

type DayMetaErrors = {
  title?: string
  windowStart?: string
  windowEnd?: string
  general?: string
}

type ItemFormErrors = {
  title?: string
  duration?: string
  notes?: string
  fixedTime?: string
  general?: string
}

type ItemDraftState = {
  title: string
  durationMinutes: number
  durationSeconds: number
  autoAdvance: boolean
  notes: string
  fixedTime: string
}

interface DayItemPrepAttachment extends ClockDayItemPrep {
  prep: PrepDetailResponse | null
}

type ExpectedStartInfo = { startEpochMs: number | null; label: string }

interface ItemPrepState {
  loading: boolean
  error: string | null
  preps: DayItemPrepAttachment[]
  cache: Record<number, PrepDetailResponse>
}

interface SnackbarState {
  message: string
  severity: 'success' | 'error'
}

type DeleteDialogStep = 'initial' | 'liveConfirm'

const useItemSensors = () =>
  useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: SORTABLE_DRAG_DISTANCE } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

const decomposeDurationSeconds = (value: number | null | undefined): { minutes: number; seconds: number } => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return { minutes: 0, seconds: 0 }
  }

  const safeValue = Math.max(0, Math.floor(value))
  const minutes = Math.floor(safeValue / 60)
  const seconds = safeValue % 60

  return { minutes, seconds }
}

const toCreationDefaultDate = (): string => {
  const now = new Date()
  return now.toISOString().slice(0, 10)
}

const ShowClockDaysPage: React.FC = () => {
  const { me, activeStation } = useOutletContext<AppOutletContext>()
  usePageTitle('Daily Clocks', { showBreadcrumbs: false })

  const stations = me.stations ?? []
  const stationId = activeStation?.id ?? null
  const stationTimezone = activeStation?.timezone ?? null
  const [clockMeta, setClockMeta] = React.useState<ClockMetaResponse | null>(null)
  const itemTypes = React.useMemo(() => resolveClockItemTypes(clockMeta), [clockMeta])
  const itemTypeMap = React.useMemo(() => {
    const map = new Map<string, ClockItemTypeMeta>()
    itemTypes.forEach((item) => map.set(item.key, item))
    return map
  }, [itemTypes])

  const [templates, setTemplates] = React.useState<ClockTemplateSummary[]>([])
  const [templatesLoading, setTemplatesLoading] = React.useState(false)
  const [templatesError, setTemplatesError] = React.useState<string | null>(null)

  const [days, setDays] = React.useState<ClockDay[]>([])
  const [daysLoading, setDaysLoading] = React.useState(false)
  const [daysError, setDaysError] = React.useState<string | null>(null)

  const [selectedDayId, setSelectedDayId] = React.useState<number | null>(null)
  const [dayDetail, setDayDetail] = React.useState<ClockDay | null>(null)
  const [dayItems, setDayItems] = React.useState<ClockDayItem[]>([])
  const [dayDetailLoading, setDayDetailLoading] = React.useState(false)
  const [dayDetailError, setDayDetailError] = React.useState<string | null>(null)

  const [dayMetaDraft, setDayMetaDraft] = React.useState<DayMetaDraft>({
    title: '',
    windowStart: '',
    windowEnd: '',
  })
  const [dayMetaErrors, setDayMetaErrors] = React.useState<DayMetaErrors>({})
  const [dayMetaSaving, setDayMetaSaving] = React.useState(false)

  const [creationForm, setCreationForm] = React.useState({
    templateId: '',
    date: toCreationDefaultDate(),
    windowStart: '06:00',
    windowEnd: '09:00',
    title: '',
  })
  const [creationError, setCreationError] = React.useState<string | null>(null)
  const [creating, setCreating] = React.useState(false)

  const [creationDialogOpen, setCreationDialogOpen] = React.useState(false)

  const [selectedItemId, setSelectedItemId] = React.useState<number | null>(null)
  const [itemDraft, setItemDraft] = React.useState<ItemDraftState>({
    title: '',
    durationMinutes: 0,
    durationSeconds: 0,
    autoAdvance: false,
    notes: '',
    fixedTime: '',
  })
  const [itemErrors, setItemErrors] = React.useState<ItemFormErrors>({})
  const [itemSaving, setItemSaving] = React.useState(false)
  const [notesOpenByItemId, setNotesOpenByItemId] = React.useState<Record<number, boolean>>({})
  const notesInputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const previousNotesOpenRef = React.useRef(false)

  const [prepAttachments, setPrepAttachments] = React.useState<Record<number, ItemPrepState>>({})
  const prepCacheRef = React.useRef<Map<number, PrepDetailResponse>>(new Map())
  const [prepDrawerOpen, setPrepDrawerOpen] = React.useState(false)
  const [activePrepId, setActivePrepId] = React.useState<number | null>(null)
  const lastInvokerRef = React.useRef<HTMLElement | null>(null)
  const [isPrepPickerOpen, setIsPrepPickerOpen] = React.useState(false)
  const prepPickerApi = React.useMemo(
    () => ({
      listMyPrepItems,
      searchPrepItems,
    }),
    [listMyPrepItems, searchPrepItems],
  )
  const openPrepDrawer = React.useCallback((id: number, invoker?: HTMLElement | null) => {
    setActivePrepId(id)
    lastInvokerRef.current = invoker ?? null
    setPrepDrawerOpen(true)
  }, [])

  const closePrepDrawer = React.useCallback(() => {
    setPrepDrawerOpen(false)
    queueMicrotask(() => {
      lastInvokerRef.current?.focus()
    })
    setActivePrepId(null)
  }, [])
  const [itemMenuAnchor, setItemMenuAnchor] = React.useState<null | HTMLElement>(null)

  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)

  const [deleteTarget, setDeleteTarget] = React.useState<ClockDay | null>(null)
  const [deleteStep, setDeleteStep] = React.useState<DeleteDialogStep>('initial')
  const [deleteLoading, setDeleteLoading] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)

  const itemSensors = useItemSensors()
  const prepSensors = useItemSensors()

  const orderedDayItems = React.useMemo(
    () =>
      dayItems.map((item) => ({
        id: item.id,
        item_type: item.item_type,
        title: item.title,
      })),
    [dayItems],
  )
  const displayTitlesById = useDisplayTitles(orderedDayItems)
  const expectedStartsById = React.useMemo(() => {
    const result: Record<number, ExpectedStartInfo> = {}
    if (dayItems.length === 0) {
      return result
    }

    const stationTz = stationTimezone ?? ''
    const windowStart = dayDetail?.window_start
    const initialCursor = windowStart ? stationLocalDateTimeToEpochMs(windowStart, stationTz) : null

    if (initialCursor === null) {
      dayItems.forEach((item) => {
        result[item.id] = { startEpochMs: null, label: '—' }
      })
      return result
    }

    let cursor: number | null = initialCursor

    dayItems.forEach((item) => {
      if (item.fixed_time) {
        const fixedEpoch = stationLocalDateTimeToEpochMs(item.fixed_time, stationTz)
        if (fixedEpoch !== null) {
          cursor = fixedEpoch
        }
      }

      const startEpoch = cursor
      const label = startEpoch !== null ? formatClockTimeLabel24s(startEpoch, stationTz) : '—'

      result[item.id] = { startEpochMs: startEpoch, label }

      const durationMs = Math.max(0, item.planned_duration_sec ?? 0) * 1000
      if (cursor !== null) {
        cursor += durationMs
      }
    })

    return result
  }, [dayDetail?.window_start, dayItems, stationTimezone])

  const upcomingDays = React.useMemo(() => {
    if (days.length === 0) {
      return []
    }
    const nowEpoch = Date.now()
    return days.filter((day) => isUpcomingDay(day, stationTimezone, nowEpoch))
  }, [days, stationTimezone])

  const selectedDayOption = React.useMemo(
    () => upcomingDays.find((day) => day.id === selectedDayId) ?? null,
    [selectedDayId, upcomingDays],
  )

  const hasUpcomingDays = upcomingDays.length > 0
  const showEmptyHelper = !hasUpcomingDays && !daysLoading && !daysError

  React.useEffect(() => {
    if (!creationDialogOpen) {
      setCreationError(null)
    }
  }, [creationDialogOpen])

  React.useEffect(() => {
    if (!deleteTarget) {
      setDeleteStep('initial')
      setDeleteError(null)
    }
  }, [deleteTarget])

  React.useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    const loadMeta = async () => {
      try {
        const response = await getClockMeta({ signal: controller.signal })
        if (!cancelled) {
          setClockMeta(response)
        }
      } catch (error) {
        if (!cancelled) {
          setClockMeta(null)
        }
      }
    }

    loadMeta()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [])

  React.useEffect(() => {
    if (!stationId) {
      setTemplates([])
      setTemplatesError(null)
      return
    }

    let cancelled = false
    const controller = new AbortController()

    const loadTemplates = async () => {
      setTemplatesLoading(true)
      setTemplatesError(null)
      try {
        const response = await listClockTemplates(
          {
            stationId,
            page: 1,
            limit: 50,
          },
          { signal: controller.signal },
        )
        if (!cancelled) {
          setTemplates(response.templates)
        }
      } catch (error) {
        if (!cancelled) {
          setTemplatesError(getErrorMessage(error, 'Unable to load templates.'))
          setTemplates([])
        }
      } finally {
        if (!cancelled) {
          setTemplatesLoading(false)
        }
      }
    }

    loadTemplates()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [stationId])

  React.useEffect(() => {
    if (!stationId) {
      setDays([])
      setSelectedDayId(null)
      return
    }

    let cancelled = false
    const controller = new AbortController()

    const loadDays = async () => {
      setDaysLoading(true)
      setDaysError(null)
      try {
        const response = await listClockDays({ stationId }, { signal: controller.signal })
        if (cancelled) {
          return
        }
        const sorted = response.days.slice().sort(compareDays)
        setDays(sorted)
        if (sorted.length === 0) {
          setSelectedDayId(null)
        }
      } catch (error) {
        if (!cancelled) {
          setDaysError(getErrorMessage(error, 'Unable to load daily clocks.'))
          setDays([])
          setSelectedDayId(null)
        }
      } finally {
        if (!cancelled) {
          setDaysLoading(false)
        }
      }
    }

    loadDays()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [stationId, stationTimezone])

  React.useEffect(() => {
    if (upcomingDays.length === 0) {
      setSelectedDayId(null)
      return
    }
    setSelectedDayId((prev) => {
      if (prev && upcomingDays.some((day) => day.id === prev)) {
        return prev
      }
      return upcomingDays[0]?.id ?? null
    })
  }, [upcomingDays])

  const loadDayDetail = React.useCallback(
    async (stationId: number, dayId: number, signal: AbortSignal) => {
      setDayDetailLoading(true)
      setDayDetailError(null)
      try {
        const response = await getClockDay(stationId, dayId, { signal })
        setDayDetail(response.day)
        setDayItems(response.items)
        setDayMetaDraft({
          title: response.day.title ?? '',
          windowStart: extractTime(response.day.window_start),
          windowEnd: extractTime(response.day.window_end),
        })
      } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') {
          return
        }
        setDayDetailError(getErrorMessage(error, 'Unable to load clock day.'))
        setDayDetail(null)
        setDayItems([])
      } finally {
        setDayDetailLoading(false)
      }
    },
    [],
  )

  React.useEffect(() => {
    if (!stationId || !selectedDayId) {
      setDayDetail(null)
      setDayItems([])
      return
    }
    const controller = new AbortController()
    void loadDayDetail(stationId, selectedDayId, controller.signal)
    return () => controller.abort()
  }, [loadDayDetail, selectedDayId, stationId])

  React.useEffect(() => {
    if (dayItems.length === 0) {
      setSelectedItemId(null)
      return
    }
    setSelectedItemId((prev) => {
      if (prev && dayItems.some((item) => item.id === prev)) {
        return prev
      }
      return dayItems[0]?.id ?? null
    })
  }, [dayItems])

  const selectedItem = React.useMemo(
    () => dayItems.find((item) => item.id === selectedItemId) ?? null,
    [dayItems, selectedItemId],
  )

  const attachedPrepItemIds = React.useMemo(
    () => (selectedItem ? (prepAttachments[selectedItem.id]?.preps ?? []).map((prep) => prep.prep_item_id) : []),
    [prepAttachments, selectedItem],
  )

  React.useEffect(() => {
    if (!selectedItem) {
      setItemMenuAnchor(null)
    }
  }, [selectedItem])

  React.useEffect(() => {
    if (!selectedItem) {
      setIsPrepPickerOpen(false)
    }
  }, [selectedItem])

  const selectedExpectedStartLabel =
    selectedItem ? expectedStartsById[selectedItem.id]?.label ?? '—' : '—'

  const isNotesOpen = selectedItem
    ? notesOpenByItemId[selectedItem.id] ?? Boolean(selectedItem.notes?.trim())
    : false
  const hasDraftNotes = Boolean(itemDraft.notes.trim())

  const handleToggleNotes = React.useCallback((itemId: number, open: boolean) => {
    setNotesOpenByItemId((prev) => ({
      ...prev,
      [itemId]: open,
    }))
  }, [])

  const handleNotesInputRef = React.useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement | null) => {
      notesInputRef.current = element
    },
    [],
  )

  const handleOpenItemMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setItemMenuAnchor(event.currentTarget)
  }, [])

  const handleCloseItemMenu = React.useCallback(() => {
    setItemMenuAnchor(null)
  }, [])

  const handleInsertItemAfter = async () => {
    if (!stationId || !selectedDayId || !selectedItem) {
      handleCloseItemMenu()
      return
    }

    handleCloseItemMenu()

    const meta = itemTypeMap.get(selectedItem.item_type)
    const baseDuration =
      typeof meta?.defaultDurationSeconds === 'number'
        ? meta.defaultDurationSeconds
        : selectedItem.planned_duration_sec
    const safeDuration = Number.isFinite(baseDuration)
      ? Math.max(0, Math.floor(baseDuration))
      : 0
    const previousIds = new Set(dayItems.map((item) => item.id))
    const currentIndex = dayItems.findIndex((item) => item.id === selectedItem.id)

    try {
      await createClockDayItem(stationId, {
        day_id: selectedDayId,
        item_type: selectedItem.item_type,
        planned_duration_sec: safeDuration,
        after_item_id: selectedItem.id,
      })
      const itemsResponse = await listClockDayItems(stationId, selectedDayId)
      const refreshedItems = itemsResponse.items
      setDayItems(refreshedItems)
      const insertedItem = refreshedItems.find((item) => !previousIds.has(item.id))
      if (insertedItem) {
        setSelectedItemId(insertedItem.id)
      } else if (currentIndex >= 0) {
        const fallback = refreshedItems[Math.min(currentIndex + 1, refreshedItems.length - 1)]
        setSelectedItemId(fallback?.id ?? null)
      }
      setSnackbar({ message: 'Item inserted.', severity: 'success' })
    } catch (error) {
      setSnackbar({ message: getErrorMessage(error, 'Unable to insert item.'), severity: 'error' })
    }
  }

  const handleDeleteSelectedItem = async () => {
    if (!stationId || !selectedDayId || !selectedItem) {
      handleCloseItemMenu()
      return
    }

    handleCloseItemMenu()

    const currentIndex = dayItems.findIndex((item) => item.id === selectedItem.id)

    try {
      await deleteClockDayItem(stationId, selectedItem.id)
      const itemsResponse = await listClockDayItems(stationId, selectedDayId)
      const refreshedItems = itemsResponse.items
      setDayItems(refreshedItems)
      const nextIndex = currentIndex > 0 ? currentIndex - 1 : 0
      const nextItem = refreshedItems[nextIndex] ?? null
      setSelectedItemId(nextItem?.id ?? null)
      setNotesOpenByItemId((prev) => {
        if (!(selectedItem.id in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[selectedItem.id]
        return next
      })
      setPrepAttachments((prev) => {
        if (!(selectedItem.id in prev)) {
          return prev
        }
        const next = { ...prev }
        delete next[selectedItem.id]
        return next
      })
      setSnackbar({ message: 'Item deleted.', severity: 'success' })
    } catch (error) {
      setSnackbar({ message: getErrorMessage(error, 'Unable to delete item.'), severity: 'error' })
    }
  }

  React.useEffect(() => {
    if (selectedItem) {
      const { minutes, seconds } = decomposeDurationSeconds(selectedItem.planned_duration_sec)
      setItemDraft({
        title: selectedItem.title ?? '',
        durationMinutes: minutes,
        durationSeconds: seconds,
        autoAdvance: Boolean(selectedItem.auto_advance),
        notes: selectedItem.notes ?? '',
        fixedTime: extractTime(selectedItem.fixed_time),
      })
      setItemErrors({})
    } else {
      setItemDraft({ title: '', durationMinutes: 0, durationSeconds: 0, autoAdvance: false, notes: '', fixedTime: '' })
      setItemErrors({})
    }
  }, [selectedItem])

  React.useEffect(() => {
    if (!selectedItem) {
      previousNotesOpenRef.current = false
      return
    }
    const defaultOpen = Boolean(selectedItem.notes?.trim())
    setNotesOpenByItemId((prev) => {
      const current = prev[selectedItem.id]
      if (current === defaultOpen) {
        return prev
      }
      return {
        ...prev,
        [selectedItem.id]: defaultOpen,
      }
    })
    previousNotesOpenRef.current = defaultOpen
  }, [selectedItem?.id, selectedItem?.notes])

  React.useEffect(() => {
    if (!selectedItem) {
      return
    }
    if (isNotesOpen && !previousNotesOpenRef.current) {
      notesInputRef.current?.focus()
    }
    previousNotesOpenRef.current = isNotesOpen
  }, [isNotesOpen, selectedItem?.id])

  const loadPrepsForItem = React.useCallback(
    async (stationId: number, itemId: number, signal: AbortSignal) => {
      setPrepAttachments((prev) => ({
        ...prev,
        [itemId]: {
          loading: true,
          error: null,
          preps: prev[itemId]?.preps ?? [],
          cache: prev[itemId]?.cache ?? {},
        },
      }))
      try {
        const response = await listClockDayItemPreps(stationId, itemId, { signal })
        const attachments: DayItemPrepAttachment[] = []
        for (const prep of response.preps) {
          let detail: PrepDetailResponse | null = prepCacheRef.current.get(prep.prep_item_id) ?? null
          if (!detail) {
            try {
              const fetchedDetail = await fetchPrepItemDetail(prep.prep_item_id, { signal })
              detail = fetchedDetail
              prepCacheRef.current.set(prep.prep_item_id, fetchedDetail)
            } catch (error) {
              if ((error as DOMException)?.name === 'AbortError') {
                throw error
              }
              detail = null
            }
          }
          attachments.push({ ...prep, prep: detail })
        }
        setPrepAttachments((prev) => {
          const previous = prev[itemId]
          const cache = { ...(previous?.cache ?? {}) }
          for (const attachment of attachments) {
            if (attachment.prep) {
              cache[attachment.prep_item_id] = attachment.prep
            }
          }
          return {
            ...prev,
            [itemId]: {
              loading: false,
              error: null,
              preps: attachments,
              cache,
            },
          }
        })
      } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') {
          return
        }
        setPrepAttachments((prev) => ({
          ...prev,
          [itemId]: {
            loading: false,
            error: getErrorMessage(error, 'Unable to load prep attachments.'),
            preps: prev[itemId]?.preps ?? [],
            cache: prev[itemId]?.cache ?? {},
          },
        }))
      }
    },
    [],
  )

  React.useEffect(() => {
    if (!stationId || !selectedItemId) {
      return
    }
    const controller = new AbortController()
    void loadPrepsForItem(stationId, selectedItemId, controller.signal)
    return () => controller.abort()
  }, [loadPrepsForItem, selectedItemId, stationId])

  const handleCreateDay = async (event: React.FormEvent) => {
    event.preventDefault()
    if (creating) {
      return
    }
    if (!stationId) {
      setCreationError('Select a station before creating a day.')
      return
    }
    if (!creationForm.templateId) {
      setCreationError('Choose a template to clone from.')
      return
    }
    if (!creationForm.date) {
      setCreationError('Pick a date for this running order.')
      return
    }
    if (!creationForm.windowStart || !creationForm.windowEnd) {
      setCreationError('Provide the show window start and end time.')
      return
    }

    setCreating(true)
    setCreationError(null)
    try {
      const response = await createClockDay(
        {
          station_id: stationId,
          template_id: Number(creationForm.templateId),
          date: creationForm.date,
          window_start: creationForm.windowStart,
          window_end: creationForm.windowEnd,
          title: creationForm.title.trim() ? creationForm.title.trim() : undefined,
        },
        {},
      )
      setDays((prev) => [response.day, ...prev.filter((day) => day.id !== response.day.id)].sort(compareDays))
      setSelectedDayId(response.day.id)
      setDayDetail(response.day)
      setDayItems(response.items)
      setDayMetaDraft({
        title: response.day.title ?? '',
        windowStart: extractTime(response.day.window_start),
        windowEnd: extractTime(response.day.window_end),
      })
      setCreationForm((prev) => ({ ...prev, title: '' }))
      setCreationDialogOpen(false)
      setSnackbar({ message: 'Daily clock created.', severity: 'success' })
    } catch (error) {
      setCreationError(getErrorMessage(error, 'Unable to create daily clock.'))
    } finally {
      setCreating(false)
    }
  }

  const handleRequestDeleteDay = () => {
    if (!dayDetail) {
      return
    }
    setDeleteTarget(dayDetail)
    setDeleteStep('initial')
    setDeleteError(null)
  }

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) {
      return
    }
    setDeleteTarget(null)
  }

  const handleConfirmDeleteDay = async () => {
    if (!deleteTarget) {
      return
    }
    if (deleteLoading) {
      return
    }
    const targetStationId = deleteTarget.station_id
    const isCurrentStation = stationId === targetStationId

    setDeleteLoading(true)
    setDeleteError(null)

    try {
      if (deleteStep === 'initial') {
        const lookup = await lookupClockDay({ stationId })
        if (lookup.current?.id === deleteTarget.id) {
          setDeleteStep('liveConfirm')
          setDeleteLoading(false)
          return
        }
      }

      await deleteClockDay(targetStationId, deleteTarget.id)

      if (isCurrentStation) {
        let nextSelectedId: number | null = selectedDayId
        setDays((prev) => {
          const filtered = prev.filter((day) => day.id !== deleteTarget.id)
          if (selectedDayId === deleteTarget.id) {
            nextSelectedId = filtered[0]?.id ?? null
          } else if (selectedDayId && !filtered.some((day) => day.id === selectedDayId)) {
            nextSelectedId = filtered[0]?.id ?? null
          }
          return filtered
        })

        setSelectedDayId(nextSelectedId ?? null)
        setDayDetail(null)
        setDayItems([])
        setDayMetaDraft({ title: '', windowStart: '', windowEnd: '' })
        setDayMetaErrors({})
        setSelectedItemId(null)
        setPrepAttachments({})
      }
      setDeleteTarget(null)
      setSnackbar({ message: 'Daily clock deleted.', severity: 'success' })
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to delete daily clock.')
      setDeleteError(message)
      setSnackbar({ message, severity: 'error' })
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleSaveDayMeta = async () => {
    if (!stationId || !dayDetail) {
      return
    }
    const errors: DayMetaErrors = {}
    if (dayMetaDraft.windowStart.trim() === '') {
      errors.windowStart = 'Window start is required.'
    }
    if (dayMetaDraft.windowEnd.trim() === '') {
      errors.windowEnd = 'Window end is required.'
    }
    setDayMetaErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    const payload: { title?: string | null; window_start?: string | null; window_end?: string | null } = {}
    const trimmedTitle = dayMetaDraft.title.trim()
    if (trimmedTitle !== (dayDetail.title ?? '')) {
      payload.title = trimmedTitle ? trimmedTitle : null
    }
    const existingStart = extractTime(dayDetail.window_start)
    if (dayMetaDraft.windowStart !== existingStart) {
      payload.window_start = dayMetaDraft.windowStart
    }
    const existingEnd = extractTime(dayDetail.window_end)
    if (dayMetaDraft.windowEnd !== existingEnd) {
      payload.window_end = dayMetaDraft.windowEnd
    }

    setDayMetaSaving(true)
    try {
      const response = await updateClockDay(stationId, dayDetail.id, payload, {})
      setDayDetail(response.day)
      setDayItems(response.items)
      setDays((prev) => {
        const next = prev.filter((day) => day.id !== response.day.id)
        next.push(response.day)
        next.sort(compareDays)
        return next
      })
      setSnackbar({ message: 'Day details saved.', severity: 'success' })
    } catch (error) {
      setDayMetaErrors({ general: getErrorMessage(error, 'Unable to save day details.') })
    } finally {
      setDayMetaSaving(false)
    }
  }

  const persistItemOrder = React.useCallback(
    (previous: ClockDayItem[], nextItems: ClockDayItem[]) => {
      if (!stationId || !selectedDayId) {
        return
      }
      void (async () => {
        try {
          const response = await reorderClockDayItems(stationId, selectedDayId, {
            order: nextItems.map((item) => item.id),
          })
          setDayItems(response.items)
        } catch (error) {
          setDayItems(previous)
          setSnackbar({ message: getErrorMessage(error, 'Unable to reorder items.'), severity: 'error' })
        }
      })()
    },
    [selectedDayId, stationId],
  )

  const handleItemDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (!stationId || !selectedDayId) {
        return
      }
      const { active, over } = event
      if (!over || active.id === over.id) {
        return
      }
      setDayItems((prev) => {
        const currentIndex = prev.findIndex((item) => item.id === Number(active.id))
        const targetIndex = prev.findIndex((item) => item.id === Number(over.id))
        if (currentIndex === -1 || targetIndex === -1) {
          return prev
        }
        const nextItems = arrayMove(prev, currentIndex, targetIndex)
        persistItemOrder(prev, nextItems)
        return nextItems
      })
    },
    [persistItemOrder, selectedDayId, stationId],
  )

  const handleSaveItem = async () => {
    if (!stationId || !selectedItem) {
      return
    }
    const errors: ItemFormErrors = {}
    const durationMinutes = Number.isFinite(itemDraft.durationMinutes)
      ? Math.max(0, Math.floor(itemDraft.durationMinutes))
      : 0
    const durationSeconds = Number.isFinite(itemDraft.durationSeconds)
      ? Math.max(0, Math.min(59, Math.floor(itemDraft.durationSeconds)))
      : 0
    setItemDraft((prev) => ({
      ...prev,
      durationMinutes,
      durationSeconds,
    }))

    const totalDurationSeconds = durationMinutes * 60 + durationSeconds
    if (!Number.isFinite(totalDurationSeconds)) {
      errors.duration = 'Set a duration using minutes and seconds.'
    }
    setItemErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    const payload: {
      title?: string | null
      planned_duration_sec?: number
      notes?: string | null
      fixed_time?: string | null
      auto_advance?: boolean | null
    } = {}

    const trimmedTitle = itemDraft.title.trim()
    if (trimmedTitle !== (selectedItem.title ?? '')) {
      payload.title = trimmedTitle ? trimmedTitle : null
    }
    if (totalDurationSeconds !== selectedItem.planned_duration_sec) {
      payload.planned_duration_sec = totalDurationSeconds
    }
    const trimmedNotes = itemDraft.notes.trim()
    if (trimmedNotes !== (selectedItem.notes ?? '')) {
      payload.notes = trimmedNotes ? trimmedNotes : null
    }
    const existingFixed = extractTime(selectedItem.fixed_time)
    if (itemDraft.fixedTime !== existingFixed) {
      payload.fixed_time = itemDraft.fixedTime ? itemDraft.fixedTime : null
    }
    if (itemDraft.autoAdvance !== Boolean(selectedItem.auto_advance)) {
      payload.auto_advance = itemDraft.autoAdvance
    }

    setItemSaving(true)
    try {
      const response = await updateClockDayItem(stationId, selectedItem.id, payload, {})
      setDayItems((prev) => prev.map((item) => (item.id === response.item.id ? response.item : item)))
      setSelectedItemId(response.item.id)
      setSnackbar({ message: 'Item updated.', severity: 'success' })
    } catch (error) {
      setItemErrors({ general: getErrorMessage(error, 'Unable to save item changes.') })
    } finally {
      setItemSaving(false)
    }
  }

  const handleAttachPrep = React.useCallback(
    async (prepItemId: number) => {
      if (!stationId || !selectedItemId) {
        throw new Error('Select an item before attaching prep.')
      }
      try {
        const response = await attachClockDayItemPrep(stationId, selectedItemId, { prep_item_id: prepItemId })
        let detail: PrepDetailResponse | null = prepCacheRef.current.get(prepItemId) ?? null
        if (!detail) {
          try {
            const fetchedDetail = await fetchPrepItemDetail(prepItemId)
            detail = fetchedDetail
            prepCacheRef.current.set(prepItemId, fetchedDetail)
          } catch (error) {
            detail = null
          }
        }
        setPrepAttachments((prev) => {
          const existing =
            prev[selectedItemId] ?? ({ loading: false, error: null, preps: [], cache: {} } as ItemPrepState)
          const cache = detail ? { ...existing.cache, [prepItemId]: detail } : existing.cache
          return {
            ...prev,
            [selectedItemId]: {
              ...existing,
              preps: [...existing.preps, { ...response.prep, prep: detail }],
              loading: false,
              error: null,
              cache,
            },
          }
        })
        setSnackbar({ message: 'Prep attached.', severity: 'success' })
      } catch (error) {
        const message = getErrorMessage(error, 'Unable to attach prep.')
        setSnackbar({ message, severity: 'error' })
        throw new Error(message)
      }
    },
    [selectedItemId, stationId],
  )

  const handleDetachPrep = async (prepId: number) => {
    if (!stationId || !selectedItemId) {
      return
    }
    try {
      await detachClockDayItemPrep(stationId, prepId)
      setPrepAttachments((prev) => {
        const existing = prev[selectedItemId]
        if (!existing) {
          return prev
        }
        const nextCache = { ...existing.cache }
        const target = existing.preps.find((prep) => prep.id === prepId)
        if (target) {
          delete nextCache[target.prep_item_id]
        }
        return {
          ...prev,
          [selectedItemId]: {
            ...existing,
            preps: existing.preps.filter((prep) => prep.id !== prepId),
            cache: nextCache,
          },
        }
      })
      setSnackbar({ message: 'Prep removed.', severity: 'success' })
    } catch (error) {
      setSnackbar({ message: getErrorMessage(error, 'Unable to remove prep.'), severity: 'error' })
    }
  }

  const updateCachedPrepMembership = React.useCallback(
    (prepId: number, isInMyPrep: boolean) => {
      setPrepAttachments((prev) => {
        let mutated = false
        const nextState: Record<number, ItemPrepState> = {}
        for (const [key, value] of Object.entries(prev)) {
          const numericKey = Number(key)
          if (!Number.isFinite(numericKey)) {
            continue
          }
          let changed = false
          const updatedPreps = value.preps.map((attachment) => {
            if (attachment.prep_item_id !== prepId || !attachment.prep) {
              return attachment
            }
            if (attachment.prep.is_in_my_prep === isInMyPrep) {
              return attachment
            }
            changed = true
            return { ...attachment, prep: { ...attachment.prep, is_in_my_prep: isInMyPrep } }
          })
          const cachedDetail = value.cache[prepId]
          let nextCache = value.cache
          if (cachedDetail && cachedDetail.is_in_my_prep !== isInMyPrep) {
            changed = true
            nextCache = { ...value.cache, [prepId]: { ...cachedDetail, is_in_my_prep: isInMyPrep } }
          }
          if (changed) {
            mutated = true
            nextState[numericKey] = {
              ...value,
              preps: updatedPreps,
              cache: nextCache,
            }
          } else {
            nextState[numericKey] = value
          }
        }
        return mutated ? nextState : prev
      })
      const existingDetail = prepCacheRef.current.get(prepId)
      if (existingDetail && existingDetail.is_in_my_prep !== isInMyPrep) {
        prepCacheRef.current.set(prepId, { ...existingDetail, is_in_my_prep: isInMyPrep })
      }
    },
    [],
  )

  const handlePrepDetailLoaded = React.useCallback(
    (detail: PrepDetailResponse) => {
      prepCacheRef.current.set(detail.id, detail)
      setPrepAttachments((prev) => {
        let mutated = false
        const nextState: Record<number, ItemPrepState> = {}
        for (const [key, value] of Object.entries(prev)) {
          const numericKey = Number(key)
          if (!Number.isFinite(numericKey)) {
            continue
          }
          let changed = false
          const updatedPreps = value.preps.map((attachment) => {
            if (attachment.prep_item_id !== detail.id) {
              return attachment
            }
            changed = true
            return { ...attachment, prep: detail }
          })
          const cachedDetail = value.cache[detail.id]
          const cacheChanged = !cachedDetail || cachedDetail !== detail
          if (cacheChanged) {
            changed = true
          }
          if (changed) {
            mutated = true
            nextState[numericKey] = {
              ...value,
              preps: updatedPreps,
              cache: { ...value.cache, [detail.id]: detail },
            }
          } else {
            nextState[numericKey] = value
          }
        }
        return mutated ? nextState : prev
      })
    },
    [],
  )

  const handleTogglePrepFromDrawer = React.useCallback(
    async (next: boolean) => {
      if (!activePrepId) {
        return
      }
      const prepId = activePrepId
      const existingDetail = prepCacheRef.current.get(prepId)
      const previousValue = existingDetail?.is_in_my_prep ?? false
      if (existingDetail && previousValue === next) {
        return
      }
      updateCachedPrepMembership(prepId, next)
      try {
        if (next) {
          await addPrepItemToMyPrep(prepId)
        } else {
          await removePrepItemFromMyPrep(prepId)
        }
      } catch (error) {
        updateCachedPrepMembership(prepId, previousValue)
        setSnackbar({ message: getErrorMessage(error, 'Unable to update My Prep.'), severity: 'error' })
      }
    },
    [activePrepId, setSnackbar, updateCachedPrepMembership],
  )

  const handlePrepDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (!stationId || !selectedItemId) {
        return
      }
      const state = prepAttachments[selectedItemId]
      if (!state) {
        return
      }
      const { active, over } = event
      if (!over || active.id === over.id) {
        return
      }
      setPrepAttachments((prev) => {
        const current = prev[selectedItemId]
        if (!current) {
          return prev
        }
        const currentIndex = current.preps.findIndex((prep) => prep.id === Number(active.id))
        const targetIndex = current.preps.findIndex((prep) => prep.id === Number(over.id))
        if (currentIndex === -1 || targetIndex === -1) {
          return prev
        }
        const nextPreps = arrayMove(current.preps, currentIndex, targetIndex)
        void (async () => {
          try {
            const response = await reorderClockDayItemPreps(stationId, selectedItemId, {
              order: nextPreps.map((prep) => prep.id),
            })
            const merged = response.preps.map((prep) => {
              const cached = nextPreps.find((entry) => entry.id === prep.id)?.prep ?? null
              return { ...prep, prep: cached }
            })
            setPrepAttachments((latest) => ({
              ...latest,
              [selectedItemId]: {
                loading: false,
                error: null,
                preps: merged,
                cache: latest[selectedItemId]?.cache ?? {},
              },
            }))
          } catch (error) {
            setPrepAttachments((latest) => ({
              ...latest,
              [selectedItemId]: {
                ...current,
                preps: current.preps,
                cache: current.cache,
              },
            }))
            setSnackbar({ message: getErrorMessage(error, 'Unable to reorder prep attachments.'), severity: 'error' })
          }
        })()
        return {
          ...prev,
          [selectedItemId]: {
            ...current,
            preps: nextPreps,
            cache: current.cache,
          },
        }
      })
    },
    [prepAttachments, selectedItemId, stationId],
  )

  const drawerCachedDetail = React.useMemo(() => {
    if (!activePrepId) {
      return null
    }
    const cached = prepCacheRef.current.get(activePrepId)
    if (cached) {
      return cached
    }
    if (selectedItemId) {
      const state = prepAttachments[selectedItemId]
      if (state?.cache?.[activePrepId]) {
        return state.cache[activePrepId]
      }
      const attachment = state?.preps.find((entry) => entry.prep_item_id === activePrepId)
      if (attachment?.prep) {
        return attachment.prep
      }
    }
    return null
  }, [activePrepId, prepAttachments, selectedItemId])

  if (!stationId) {
    return (
      <TypeChipProvider meta={clockMeta}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Daily Clocks
              </Typography>
            </Stack>
            <Alert severity="info">
              {stations.length === 0
                ? 'You need access to at least one station to manage daily clocks.'
                : 'No station selected.'}
            </Alert>
          </Stack>
        </Box>
      </TypeChipProvider>
    )
  }

  return (
    <TypeChipProvider meta={clockMeta}>
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Autocomplete<ClockDay>
              options={upcomingDays}
              value={selectedDayOption}
              onChange={(_, option) => {
                setSelectedDayId(option?.id ?? null)
              }}
              getOptionLabel={formatDayOptionLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={daysLoading}
              loadingText="Loading daily clocks…"
              noOptionsText="No upcoming clocks."
              disabled={!hasUpcomingDays && !daysLoading}
              disableClearable
              sx={{ flexGrow: 1, minWidth: { sm: 260 }, maxWidth: { sm: 360, md: 400 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Daily clock"
                  placeholder={hasUpcomingDays ? 'Select day' : undefined}
                  helperText={daysError ?? (showEmptyHelper ? 'No upcoming clocks.' : undefined)}
                  error={Boolean(daysError)}
                />
              )}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreationDialogOpen(true)}>
              Create from template
            </Button>
          </Stack>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12}>
              {dayDetailLoading ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                  <CircularProgress />
                </Paper>
              ) : dayDetail ? (
                <Stack spacing={3}>
                  <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {dayDetail.title?.trim() || `Clock for ${formatDayDate(dayDetail.date)}`}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={handleRequestDeleteDay}
                        >
                          Delete
                        </Button>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Title"
                            value={dayMetaDraft.title}
                            onChange={(event) => setDayMetaDraft((prev) => ({ ...prev, title: event.target.value }))}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            label="Window start"
                            type="time"
                            value={dayMetaDraft.windowStart}
                            onChange={(event) => setDayMetaDraft((prev) => ({ ...prev, windowStart: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(dayMetaErrors.windowStart)}
                            helperText={dayMetaErrors.windowStart ?? 'Station local time.'}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            label="Window end"
                            type="time"
                            value={dayMetaDraft.windowEnd}
                            onChange={(event) => setDayMetaDraft((prev) => ({ ...prev, windowEnd: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(dayMetaErrors.windowEnd)}
                            helperText={dayMetaErrors.windowEnd ?? 'Station local time.'}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      {dayMetaErrors.general ? <Alert severity="error">{dayMetaErrors.general}</Alert> : null}
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSaveDayMeta}
                          disabled={dayMetaSaving}
                        >
                          {dayMetaSaving ? 'Saving…' : 'Save day details'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                  <Paper sx={{ borderRadius: 3, p: 0 }}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      divider={<Divider orientation="vertical" flexItem />}
                      sx={{ minHeight: { xs: 'auto', md: 'calc(100vh - 160px)' } }}
                    >
                      <Box
                        sx={{
                          width: { xs: '100%', md: 360 },
                          display: 'flex',
                          flexDirection: 'column',
                          p: 3,
                          pr: { xs: 3, md: 2 },
                          height: { xs: 'auto', md: 'calc(100vh - 160px)' },
                          overflow: { xs: 'visible', md: 'hidden' },
                        }}
                      >
                        <Box sx={{ pb: 1.5, flexShrink: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Items
                            </Typography>
                            <Chip label={`${dayItems.length}`} size="small" />
                          </Stack>
                        </Box>
                        <Box sx={{ flex: '1 1 auto', overflowY: { xs: 'visible', md: 'auto' }, pb: 2 }}>
                          <DndContext sensors={itemSensors} onDragEnd={handleItemDragEnd}>
                            <SortableContext
                              items={dayItems.map((item) => item.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <Stack spacing={1.5}>
                                {dayItems.map((item) => (
                                  <SortableDayItemRow
                                    key={item.id}
                                    item={item}
                                    meta={itemTypeMap.get(item.item_type)}
                                    selected={item.id === selectedItemId}
                                    displayTitle={displayTitlesById[item.id] ?? item.title?.trim() ?? 'Untitled block'}
                                    expectedStartLabel={expectedStartsById[item.id]?.label ?? '—'}
                                    onSelect={() => setSelectedItemId(item.id)}
                                  />
                                ))}
                              </Stack>
                            </SortableContext>
                          </DndContext>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          flexGrow: 1,
                          p: 3,
                          maxHeight: 'none',
                          overflow: 'visible',
                        }}
                      >
                        {selectedItem ? (
                          <Stack spacing={3}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                              sx={{ flexWrap: 'wrap', rowGap: 1.5, width: '100%', mb: 1 }}
                            >
                              <TypeChip
                                typeKey={selectedItem.item_type}
                                label={itemTypeMap.get(selectedItem.item_type)?.label}
                                size="small"
                              />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {displayTitlesById[selectedItem.id] ?? selectedItem.title?.trim() ?? 'Untitled block'}
                              </Typography>
                              <Tooltip title="Expected station-local start time">
                                <Chip label={`Expected start ${selectedExpectedStartLabel}`} size="small" />
                              </Tooltip>
                              <Tooltip title="Planned duration">
                                <Chip label={formatClockDuration(selectedItem.planned_duration_sec)} size="small" />
                              </Tooltip>
                              {selectedItem.fixed_time ? (
                                <Chip
                                  icon={<AccessTimeIcon fontSize="small" />}
                                  label={extractTime(selectedItem.fixed_time)}
                                  size="small"
                                />
                              ) : null}
                              <IconButton
                                aria-label="Item actions"
                                onClick={handleOpenItemMenu}
                                sx={{ marginLeft: { xs: 0, md: 'auto' } }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Stack>
                            <Menu
                              anchorEl={itemMenuAnchor}
                              open={Boolean(itemMenuAnchor)}
                              onClose={handleCloseItemMenu}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                              <MenuItem onClick={handleInsertItemAfter}>Insert new item after this</MenuItem>
                              <MenuItem onClick={handleDeleteSelectedItem}>Delete item</MenuItem>
                            </Menu>
                            <Grid container spacing={2} alignItems="flex-start">
                              <Grid item xs={12}>
                                <TextField
                                  label="Title"
                                  value={itemDraft.title}
                                  onChange={(event) => setItemDraft((prev) => ({ ...prev, title: event.target.value }))}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <ClockDurationFields
                                  minutes={itemDraft.durationMinutes}
                                  seconds={itemDraft.durationSeconds}
                                  onChange={({ minutes, seconds }) =>
                                    setItemDraft((prev) => ({
                                      ...prev,
                                      durationMinutes: minutes,
                                      durationSeconds: seconds,
                                    }))
                                  }
                                  helperText={itemErrors.duration ?? 'Minutes × 60 + seconds. Seconds step 10.'}
                                  error={Boolean(itemErrors.duration)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label="Fixed time"
                                  type="time"
                                  value={itemDraft.fixedTime}
                                  onChange={(event) => setItemDraft((prev) => ({ ...prev, fixedTime: event.target.value }))}
                                  InputLabelProps={{ shrink: true }}
                                  helperText="Optional station-local start."
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={itemDraft.autoAdvance}
                                      onChange={(event) =>
                                        setItemDraft((prev) => ({ ...prev, autoAdvance: event.target.checked }))
                                      }
                                    />
                                  }
                                  label="Auto advance"
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Stack spacing={isNotesOpen ? 1.5 : 1} sx={{ width: '100%' }}>
                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    justifyContent="space-between"
                                    sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      Notes
                                    </Typography>
                                    {selectedItem ? (
                                      <Button
                                        startIcon={
                                          isNotesOpen ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />
                                        }
                                        onClick={() => handleToggleNotes(selectedItem.id, !isNotesOpen)}
                                        size={isNotesOpen ? 'small' : 'medium'}
                                      >
                                        {isNotesOpen ? 'Hide notes' : hasDraftNotes ? 'Show notes' : 'Add notes'}
                                      </Button>
                                    ) : null}
                                  </Stack>
                                  {isNotesOpen ? (
                                    <TextField
                                      label="Notes"
                                      value={itemDraft.notes}
                                      onChange={(event) =>
                                        setItemDraft((prev) => ({ ...prev, notes: event.target.value }))
                                      }
                                      multiline
                                      minRows={3}
                                      fullWidth
                                      inputRef={handleNotesInputRef}
                                    />
                                  ) : null}
                                </Stack>
                              </Grid>
                            </Grid>
                            {itemErrors.general ? <Alert severity="error">{itemErrors.general}</Alert> : null}
                            <Stack spacing={2}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                sx={{ flexWrap: 'wrap', rowGap: 1.5 }}
                              >
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Prep Attachments
                                  </Typography>
                                  <Tooltip title="Drag to reorder">
                                    <span>
                                      <IconButton size="small" disabled>
                                        <DragIndicatorIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                                <Button
                                  variant="contained"
                                  startIcon={<AddIcon fontSize="small" />}
                                  onClick={() => setIsPrepPickerOpen(true)}
                                  disabled={!selectedItem || !stationId}
                                >
                                  Add Prep Item
                                </Button>
                              </Stack>
                              <Divider />
                              <DndContext sensors={prepSensors} onDragEnd={handlePrepDragEnd}>
                                <SortableContext
                                  items={(prepAttachments[selectedItem.id]?.preps ?? []).map((prep) => prep.id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <Stack spacing={1.5}>
                                    {(prepAttachments[selectedItem.id]?.preps ?? []).map((prep, index) => (
                                      <SortablePrepAttachmentRow
                                        key={prep.id}
                                        attachment={prep}
                                        index={index}
                                        onRemove={() => handleDetachPrep(prep.id)}
                                        onOpenDetail={openPrepDrawer}
                                      />
                                    ))}
                                  </Stack>
                                </SortableContext>
                              </DndContext>
                              {prepAttachments[selectedItem.id]?.loading ? (
                                <LinearProgress sx={{ borderRadius: 999 }} />
                              ) : null}
                              {prepAttachments[selectedItem.id]?.error ? (
                                <Alert severity="error">{prepAttachments[selectedItem.id]?.error}</Alert>
                              ) : null}
                              {prepAttachments[selectedItem.id] &&
                              prepAttachments[selectedItem.id]!.preps.length === 0 &&
                              !prepAttachments[selectedItem.id]!.loading ? (
                                <Typography color="text.secondary">No prep attached yet.</Typography>
                              ) : null}
                            </Stack>
                            <Button
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveItem}
                              disabled={itemSaving}
                              sx={{ mt: 3, alignSelf: 'flex-start' }}
                            >
                              {itemSaving ? 'Saving…' : 'Save item changes'}
                            </Button>
                          </Stack>
                        ) : (
                          <Typography color="text.secondary">Select an item to edit its details and prep.</Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              ) : dayDetailError ? (
                <Alert severity="error">{dayDetailError}</Alert>
              ) : (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                  <Typography color="text.secondary">Select a daily clock to begin editing.</Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
      </Stack>
      <Dialog
        open={creationDialogOpen}
        onClose={() => {
          if (creating) {
            return
          }
          setCreationDialogOpen(false)
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ component: 'form', onSubmit: handleCreateDay }}
      >
        <DialogTitle>Create from template</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <TextField
              select
              label="Template"
              value={creationForm.templateId}
              onChange={(event) => setCreationForm((prev) => ({ ...prev, templateId: event.target.value }))}
              fullWidth
              disabled={templatesLoading}
              helperText={templatesError ?? 'Clone an existing running order.'}
              error={Boolean(templatesError)}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
              {templates.length === 0 && !templatesLoading ? (
                <MenuItem disabled value="">
                  {templatesError ? 'Templates unavailable' : 'No templates yet'}
                </MenuItem>
              ) : null}
            </TextField>
            <TextField
              label="Date"
              type="date"
              value={creationForm.date}
              onChange={(event) => setCreationForm((prev) => ({ ...prev, date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Window start"
                type="time"
                value={creationForm.windowStart}
                onChange={(event) => setCreationForm((prev) => ({ ...prev, windowStart: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Window end"
                type="time"
                value={creationForm.windowEnd}
                onChange={(event) => setCreationForm((prev) => ({ ...prev, windowEnd: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              label="Optional title"
              value={creationForm.title}
              onChange={(event) => setCreationForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Breakfast Show with Alex"
              fullWidth
            />
            {creationError ? <Alert severity="error">{creationError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setCreationDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete daily clock</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {deleteTarget ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {deleteTarget.title?.trim() || `Clock for ${formatDayDate(deleteTarget.date)}`}
              </Typography>
            ) : null}
            <DialogContentText>
              {deleteStep === 'liveConfirm'
                ? 'This show clock is currently live. Delete anyway?'
                : 'Delete this show clock? This removes its schedule and items.'}
            </DialogContentText>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button color="error" onClick={handleConfirmDeleteDay} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting…' : deleteStep === 'liveConfirm' ? 'Delete anyway' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <PrepDetailDrawer
        open={prepDrawerOpen}
        onClose={closePrepDrawer}
        prepId={activePrepId}
        cached={drawerCachedDetail}
        fallbackItem={drawerCachedDetail}
        onToggleMyPrep={handleTogglePrepFromDrawer}
        onDetailLoaded={handlePrepDetailLoaded}
      />
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
      <PrepPicker
        open={isPrepPickerOpen}
        onClose={() => setIsPrepPickerOpen(false)}
        stationId={stationId}
        onAttach={handleAttachPrep}
        api={prepPickerApi}
        attachedItemIds={attachedPrepItemIds}
        onViewPrep={openPrepDrawer}
      />
      </Box>
    </TypeChipProvider>
  )
}

const SortableDayItemRow: React.FC<{
  item: ClockDayItem
  meta?: ClockItemTypeMeta
  selected: boolean
  displayTitle: string
  expectedStartLabel: string
  onSelect: () => void
}> = ({ item, meta, selected, displayTitle, expectedStartLabel, onSelect }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  }
  return (
    <Paper ref={setNodeRef} style={style} variant={selected ? 'outlined' : undefined} sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton
          size="small"
          color="inherit"
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label="Drag to reorder"
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <ButtonBase
          onClick={onSelect}
          sx={{
            flexGrow: 1,
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Stack spacing={0.5} alignItems="flex-start" sx={{ width: '100%' }}>
            <TypeChip typeKey={item.item_type} label={meta?.label} size="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
              {displayTitle}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Expected station-local start time">
                <Chip size="small" label={expectedStartLabel} />
              </Tooltip>
              <Tooltip title="Planned duration">
                <Chip size="small" label={formatClockDuration(item.planned_duration_sec)} />
              </Tooltip>
            </Stack>
          </Stack>
        </ButtonBase>
      </Stack>
    </Paper>
  )
}

const SortablePrepAttachmentRow: React.FC<{
  attachment: DayItemPrepAttachment
  index: number
  onRemove: () => void
  onOpenDetail: (prepId: number, invoker?: HTMLElement | null) => void
}> = ({ attachment, index, onRemove, onOpenDetail }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: attachment.id,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  }
  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <IconButton
          size="small"
          color="inherit"
          {...attributes}
          {...listeners}
          ref={setActivatorNodeRef}
          aria-label="Drag to reorder prep"
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <ButtonBase
            onClick={(event) => onOpenDetail(attachment.prep_item_id, event.currentTarget)}
            sx={{
              textAlign: 'left',
              width: '100%',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              borderRadius: 1,
              p: 0,
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {attachment.prep?.headline ?? `Prep #${attachment.prep_item_id}`}
              </Typography>
              {attachment.prep?.tease ? (
                <Typography variant="body2" color="text.secondary">
                  {attachment.prep.tease}
                </Typography>
              ) : null}
            </Box>
          </ButtonBase>
        </Box>
        <Tooltip title="Remove attachment">
          <span>
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  )
}

export default ShowClockDaysPage
